import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking, Modal, Pressable } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../src/api/client';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { AdminToolbar } from '../../src/components/AdminList';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useResponsive } from '../../src/utils/responsive';
import { titleCase, formatPhone, formatDate } from '../../src/utils/format';

/** lastSeenLocal arrives as "YYYY-MM-DD HH:MM" in the owner's timezone. */
function fmtLastSeen(s: string | null): string {
  if (!s) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return formatDate(s);
  const h = Number(m[4]);
  const h12 = h % 12 || 12;
  return `${Number(m[2])}/${Number(m[3])}/${m[1]} ${h12}:${m[5]} ${h >= 12 ? 'PM' : 'AM'}`;
}

interface CustomerRow {
  userId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  totalWashes: number;
  lifetimeSpend: number; // cents
  washesThisMonth: number;
  avgRating: number | null;
  ratingCount: number;
  lastSeenAt: string | null;
  lastSeenLocal: string | null; // YYYY-MM-DD in owner-local tz, for display
  userCreatedAt: string | null;
  activeSubscriptions: Array<{ planName: string; tierName: string; maxWashesPerDay: number; maxWashesPerMonth: number }>;
  activeBundles: Array<{ bundleName: string; tierName: string; washesRemaining: number; washesTotal: number }>;
}

type SortKey = 'name' | 'lastSeenAt' | 'totalWashes' | 'washesThisMonth' | 'lifetimeSpend' | 'avgRating';
type SortDir = 'asc' | 'desc';

export default function OwnerCustomersScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();

  /** Tiny in-page modal listing the three gift flows. React Native's
   *  Alert.alert doesn't render multi-button menus on react-native-web
   *  (browsers only get a single OK), so we build our own. */
  const [giftMenuEmail, setGiftMenuEmail] = useState<string | null>(null);
  const openGiftMenu = (email: string | null) => {
    if (!email) {
      toast.show({ type: 'error', title: 'No email', message: "This customer doesn't have an email on file." });
      return;
    }
    setGiftMenuEmail(email);
  };
  const giftMenuGo = (path: string) => {
    if (!giftMenuEmail) return;
    const e = encodeURIComponent(giftMenuEmail);
    setGiftMenuEmail(null);
    router.push(`${path}${path.includes('?') ? '&' : '?'}email=${e}` as never);
  };

  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lastSeenAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { width } = useResponsive();
  // Below this width the 6-column table crushes the name/contact column on
  // phones (Android especially), so we switch to a stacked card layout.
  const wide = width >= 720;

  const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const fetchCustomers = async (p = page, opts?: { search?: string; sortKey?: SortKey; sortDir?: SortDir }) => {
    try {
      const s = opts?.search ?? search;
      const params = new URLSearchParams({
        page: String(p), limit: '10',
        sort: opts?.sortKey ?? sortKey, order: opts?.sortDir ?? sortDir,
      });
      if (s) params.set('search', s);
      const data = await api<{ rows: CustomerRow[]; total: number }>(`/api/customers?${params}`);
      setRows(data.rows);
      setTotal(data.total);
      setPage(p);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setLoading(false); }
  };

  useFocusEffect(useCallback(() => { fetchCustomers(1); }, []));

  const toggleSort = (k: SortKey) => {
    const nextDir: SortDir = k === sortKey ? (sortDir === 'asc' ? 'desc' : 'asc') : (k === 'name' ? 'asc' : 'desc');
    setSortKey(k);
    setSortDir(nextDir);
    fetchCustomers(1, { sortKey: k, sortDir: nextDir });
  };

  if (loading) {
    return <View style={styles.loading}><Text style={{ color: colors.textSecondary }}>Loading customers...</Text></View>;
  }

  const SortHeader = ({ k, label, flex = 1, align = 'left' }: { k: SortKey; label: string; flex?: number; align?: 'left' | 'right' }) => (
    <TouchableOpacity onPress={() => toggleSort(k)} style={{ flex, flexDirection: 'row', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: 2 }}>
      <Text style={[styles.colHeader, align === 'right' && { textAlign: 'right' }]}>{label}</Text>
      {sortKey === k && (
        <Ionicons name={sortDir === 'asc' ? 'arrow-up' : 'arrow-down'} size={11} color={colors.primary} />
      )}
    </TouchableOpacity>
  );

  const SORT_OPTIONS: Array<{ k: SortKey; label: string }> = [
    { k: 'name', label: 'Name' },
    { k: 'lastSeenAt', label: 'Last seen' },
    { k: 'washesThisMonth', label: 'This mo.' },
    { k: 'totalWashes', label: 'Lifetime' },
    { k: 'lifetimeSpend', label: 'Spend' },
    { k: 'avgRating', label: 'Rating' },
  ];

  /** Name + email + phone + plan/bundle badges — shared by table rows and cards. */
  const renderContact = (r: CustomerRow) => (
    <>
      <TouchableOpacity onPress={() => router.push(`/owner/customers/${r.userId}` as never)} accessibilityRole="link" accessibilityLabel={`Open ${titleCase(r.name) || 'customer'} details`}>
        <Text style={styles.name}>{titleCase(r.name) || '(no name)'}  ›</Text>
      </TouchableOpacity>
      {r.email && (
        <TouchableOpacity onPress={() => Linking.openURL(`mailto:${r.email}`)}>
          <Text style={styles.contact}>{r.email}</Text>
        </TouchableOpacity>
      )}
      {r.phone && (
        <TouchableOpacity onPress={() => Linking.openURL(`tel:${r.phone}`)}>
          <Text style={styles.contact}>{formatPhone(r.phone)}</Text>
        </TouchableOpacity>
      )}
      {(r.activeSubscriptions.length > 0 || r.activeBundles.length > 0) && (
        <View style={styles.badgeRow}>
          {r.activeSubscriptions.map((s, i) => (
            <View key={`s${i}`} style={[styles.badge, { backgroundColor: colors.primary + '15', borderColor: colors.primary }]}>
              <Ionicons name="ribbon" size={10} color={colors.primary} />
              <Text style={[styles.badgeText, { color: colors.primary }]}>{s.planName}</Text>
            </View>
          ))}
          {r.activeBundles.map((b, i) => (
            <View key={`b${i}`} style={[styles.badge, { backgroundColor: colors.success + '15', borderColor: colors.success }]}>
              <Ionicons name="cube" size={10} color={colors.success} />
              <Text style={[styles.badgeText, { color: colors.success }]}>{b.bundleName} · {b.washesRemaining}/{b.washesTotal}</Text>
            </View>
          ))}
        </View>
      )}
    </>
  );

  const renderGift = (r: CustomerRow) => (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <TouchableOpacity
        onPress={() => router.push(`/owner/broadcast?userId=${r.userId}&name=${encodeURIComponent(titleCase(r.name) || r.email || 'customer')}` as never)}
        accessibilityLabel={`Message ${r.email ?? 'customer'}`}
        hitSlop={8}
        style={{ paddingHorizontal: 8, paddingVertical: 4 }}
      >
        <Ionicons name="chatbubble-ellipses-outline" size={20} color={colors.primary} />
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => openGiftMenu(r.email)}
        accessibilityLabel={`Gift ${r.email ?? 'customer'}`}
        hitSlop={8}
        style={{ paddingHorizontal: 8, paddingVertical: 4 }}
      >
        <Ionicons name="gift" size={20} color={colors.primary} />
      </TouchableOpacity>
    </View>
  );

  const rating = (r: CustomerRow) => r.avgRating != null ? `${r.avgRating.toFixed(1)} (${r.ratingCount})` : '—';

  /** Labeled stat cell used in the narrow card layout. */
  const Stat = ({ label, value }: { label: string; value: string }) => (
    <View style={styles.stat}>
      <Text style={styles.statLabel}>{label}</Text>
      <Text style={styles.statValue}>{value}</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={900}>
        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.title}>Customers</Text>
          <Text style={styles.subtitle}>{total} customer{total !== 1 ? 's' : ''} have paid at your locations</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm, marginBottom: spacing.sm }}>
            <Button
              title="Message"
              variant="outline"
              onPress={() => router.push('/owner/broadcast' as never)}
              icon={<Ionicons name="megaphone-outline" size={16} color={colors.primary} />}
            />
            {/* 1.4 P24: bring existing members over from another system */}
            <Button
              title="Import customers"
              variant="outline"
              onPress={() => router.push('/owner/customers/import' as never)}
              icon={<Ionicons name="cloud-upload-outline" size={16} color={colors.primary} />}
            />
          </View>

          <AdminToolbar
            search={search}
            onSearchChange={setSearch}
            onSearch={() => fetchCustomers(1)}
            total={total}
            page={page}
            limit={10}
            onPageChange={(p) => fetchCustomers(p)}
            csvEndpoint={`/api/customers?sort=${sortKey}&order=${sortDir}${search ? `&search=${encodeURIComponent(search)}` : ''}`}
            csvFilename="rinsepay-customers.csv"
            label="customer"
          />
        </Card>

        <Card style={{ marginBottom: spacing.md }}>
          {wide ? (
            <View style={styles.tableHeader}>
              <SortHeader k="name" label="Name" flex={2} />
              <SortHeader k="washesThisMonth" label="This mo." flex={1} align="right" />
              <SortHeader k="totalWashes" label="Lifetime" flex={1} align="right" />
              <SortHeader k="lifetimeSpend" label="Spend" flex={1.2} align="right" />
              <SortHeader k="avgRating" label="Rating" flex={1} align="right" />
              <SortHeader k="lastSeenAt" label="Last seen" flex={1.3} align="right" />
              <View style={{ width: 72 }} />
            </View>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sortBar}>
              {SORT_OPTIONS.map(o => {
                const active = sortKey === o.k;
                return (
                  <TouchableOpacity
                    key={o.k}
                    onPress={() => toggleSort(o.k)}
                    style={[styles.sortChip, active && { backgroundColor: colors.primary + '15', borderColor: colors.primary }]}
                  >
                    <Text style={[styles.sortChipText, active && { color: colors.primary, fontWeight: fontWeight.semibold as any }]}>{o.label}</Text>
                    {active && <Ionicons name={sortDir === 'asc' ? 'arrow-up' : 'arrow-down'} size={11} color={colors.primary} />}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}

          {rows.length === 0 && (
            <Text style={styles.empty}>No customers match.</Text>
          )}

          {rows.map(r => wide ? (
            <View key={r.userId} style={styles.row}>
              <View style={{ flex: 2, paddingRight: spacing.sm }}>{renderContact(r)}</View>
              <Text style={[styles.cell, { flex: 1, textAlign: 'right' }]}>{r.washesThisMonth}</Text>
              <Text style={[styles.cell, { flex: 1, textAlign: 'right' }]}>{r.totalWashes}</Text>
              <Text style={[styles.cell, { flex: 1.2, textAlign: 'right' }]}>{fmtMoney(r.lifetimeSpend)}</Text>
              <Text style={[styles.cell, { flex: 1, textAlign: 'right' }]}>{rating(r)}</Text>
              <Text style={[styles.cell, { flex: 1.3, textAlign: 'right' }]}>{fmtLastSeen(r.lastSeenLocal)}</Text>
              {renderGift(r)}
            </View>
          ) : (
            <View key={r.userId} style={styles.cardRow}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>{renderContact(r)}</View>
                {renderGift(r)}
              </View>
              <View style={styles.statStrip}>
                <Stat label="This mo." value={String(r.washesThisMonth)} />
                <Stat label="Lifetime" value={String(r.totalWashes)} />
                <Stat label="Spend" value={fmtMoney(r.lifetimeSpend)} />
                <Stat label="Rating" value={rating(r)} />
                <Stat label="Last seen" value={fmtLastSeen(r.lastSeenLocal)} />
              </View>
            </View>
          ))}
        </Card>
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>

      <Modal
        visible={giftMenuEmail !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setGiftMenuEmail(null)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setGiftMenuEmail(null)}>
          <Pressable style={styles.modalSheet} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Issue gift to</Text>
            <Text style={styles.modalSub} numberOfLines={1}>{giftMenuEmail}</Text>

            <TouchableOpacity style={styles.modalOption} onPress={() => giftMenuGo('/owner/subscriptions/gifts/new')}>
              <Ionicons name="gift" size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.modalOptionTitle}>Free wash</Text>
                <Text style={styles.modalOptionSub}>100% off a specific tier or self-serve minutes</Text>
              </View>
              <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>

            <TouchableOpacity style={styles.modalOption} onPress={() => giftMenuGo('/owner/subscriptions/gifts/new?kind=percent')}>
              <Ionicons name="pricetag" size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.modalOptionTitle}>% Off</Text>
                <Text style={styles.modalOptionSub}>1–99% off a wash</Text>
              </View>
              <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>

            <TouchableOpacity style={styles.modalOption} onPress={() => giftMenuGo('/owner/subscriptions/gifts/new?kind=credit')}>
              <Ionicons name="cash" size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.modalOptionTitle}>$ Credit</Text>
                <Text style={styles.modalOptionSub}>Free wash credit balance — no charge to you</Text>
              </View>
              <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>

            <TouchableOpacity style={styles.modalCancel} onPress={() => setGiftMenuEmail(null)}>
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textLight, marginTop: 4 },

  searchWrap: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6, marginTop: spacing.sm },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },

  tableHeader: { flexDirection: 'row', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.border },
  colHeader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  contact: { fontSize: fontSize.xs, color: c.primary, marginTop: 2 },
  cell: { fontSize: fontSize.xs, color: c.text },
  empty: { textAlign: 'center', color: c.textLight, padding: spacing.lg, fontSize: fontSize.sm },

  // Narrow (phone) layout
  sortBar: { flexDirection: 'row', gap: 6, paddingVertical: spacing.xs, paddingRight: spacing.sm },
  sortChip: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: spacing.sm, paddingVertical: 5, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.border, backgroundColor: c.surfaceSecondary },
  sortChipText: { fontSize: fontSize.xs, color: c.textSecondary },
  cardRow: { paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm },
  statStrip: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md, rowGap: spacing.sm },
  stat: { minWidth: 64 },
  statLabel: { fontSize: 10, color: c.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  statValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: c.text, marginTop: 1 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  badge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, borderWidth: 1 },
  badgeText: { fontSize: 10, fontWeight: fontWeight.semibold as any },

  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modalSheet: { width: '100%', maxWidth: 420, backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, gap: spacing.sm },
  modalTitle: { fontSize: fontSize.xs, color: c.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
  modalSub: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.sm },
  modalOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, padding: spacing.md, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  modalOptionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text },
  modalOptionSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  modalCancel: { padding: spacing.md, alignItems: 'center', marginTop: spacing.xs },
  modalCancelText: { color: c.textSecondary, fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
